import { useState, useEffect } from "react";
import { Dialog, DialogContent, DialogDescription, DialogHeader, DialogTitle } from "@/components/ui/dialog";
import { Button } from "@/components/ui/button";
import { Checkbox } from "@/components/ui/checkbox";
import { ScrollArea } from "@/components/ui/scroll-area";
import { Loader2, Search, Shield } from "lucide-react";
import { Input } from "@/components/ui/input";
import { z } from "zod";
import { userGroupValidators } from "@shared/validators/admin/user-groups-validators";
import { useUserGroupWithUsersQuery } from "@/api/queries/admin/UserGroupQueries";
import { useAssignUsersMutation, useRemoveUsersMutation } from "@/api/mutations/admin/UserGroupMutations";
import { useUsersQuery } from "@/api/queries/UserQueries";
import { Badge } from "@/components/ui/badge";
import { Alert, AlertDescription } from "@/components/ui/alert";

type UserGroup = z.infer<typeof userGroupValidators.userGroupSchema>;

interface ManageUsersDialogProps {
    open: boolean;
    onOpenChange: (open: boolean) => void;
    userGroup?: UserGroup | null;
}

const ManageUsersDialog = ({ open, onOpenChange, userGroup }: ManageUsersDialogProps) => {
    const [searchQuery, setSearchQuery] = useState("");
    const [selectedUsers, setSelectedUsers] = useState<Set<string>>(new Set());

    const isIdpGroup = userGroup?.externalId != null;

    // Fetch all available users
    const { data: allUsersData, isLoading: loadingAllUsers } = useUsersQuery(
        { page: "1", per_page: "100" },
    );

    // Fetch current users for this group
    const { data: groupData, isLoading: loadingGroupData } = useUserGroupWithUsersQuery(
        userGroup?.id || "",
        open && !!userGroup?.id
    );

    const assignMutation = useAssignUsersMutation();
    const removeMutation = useRemoveUsersMutation();

    // Initialize selected users when data loads
    useEffect(() => {
        if (groupData?.users) {
            setSelectedUsers(new Set(groupData.users.map((user: any) => user.id)));
        }
    }, [groupData]);

    const allUsers = allUsersData?.data || [];
    const filteredUsers = allUsers.filter(
        (user) =>
            user.name.toLowerCase().includes(searchQuery.toLowerCase()) ||
            user.email.toLowerCase().includes(searchQuery.toLowerCase()) ||
            (user.department && user.department.toLowerCase().includes(searchQuery.toLowerCase()))
    );

    const handleToggleUser = (userId: string) => {
        const newSelected = new Set(selectedUsers);
        if (newSelected.has(userId)) {
            newSelected.delete(userId);
        } else {
            newSelected.add(userId);
        }
        setSelectedUsers(newSelected);
    };

    const handleSave = async () => {
        if (!userGroup) return;

        const currentIds = new Set(groupData?.users?.map((user: any) => user.id) || []);
        const newSelectedIds = Array.from(selectedUsers);

        // Find users to add and remove
        const toAdd = newSelectedIds.filter((id) => !currentIds.has(id));
        const toRemove = Array.from(currentIds).filter((id) => !selectedUsers.has(id));

        try {
            if (toAdd.length > 0) {
                await assignMutation.mutateAsync({
                    userGroupId: userGroup.id,
                    userIds: toAdd,
                });
            }

            if (toRemove.length > 0) {
                await removeMutation.mutateAsync({
                    userGroupId: userGroup.id,
                    userIds: toRemove,
                });
            }

            onOpenChange(false);
        } catch (error) {
            console.error("Error updating users:", error);
        }
    };

    const isSaving = assignMutation.isPending || removeMutation.isPending;
    const isLoading = loadingAllUsers || loadingGroupData;

    return (
        <Dialog open={open} onOpenChange={onOpenChange}>
            <DialogContent className="max-w-2xl">
                <DialogHeader>
                    <DialogTitle>{isIdpGroup ? "View Group Users" : "Manage Users"}</DialogTitle>
                    <DialogDescription>
                        {isIdpGroup
                            ? `Users in "${userGroup?.name}" are managed by your Identity Provider.`
                            : `Select which users should be members of "${userGroup?.name}".`}
                    </DialogDescription>
                </DialogHeader>

                <div className="space-y-4">
                    {isIdpGroup && (
                        <Alert>
                            <Shield className="h-4 w-4" />
                            <AlertDescription>
                                This is an IDP-synced group. User memberships are automatically managed by your Identity Provider and sync every 10 minutes and on login. You cannot manually add or remove users.
                            </AlertDescription>
                        </Alert>
                    )}

                    {/* Search */}
                    <div className="relative">
                        <Search className="absolute left-3 top-1/2 h-4 w-4 -translate-y-1/2 text-muted-foreground" />
                        <Input
                            placeholder="Search users..."
                            value={searchQuery}
                            onChange={(e) => setSearchQuery(e.target.value)}
                            className="pl-9"
                        />
                    </div>

                    {/* User List */}
                    <ScrollArea className="h-[400px] rounded-md border p-4">
                        {isLoading ? (
                            <div className="flex items-center justify-center py-8">
                                <Loader2 className="h-6 w-6 animate-spin" />
                            </div>
                        ) : filteredUsers.length === 0 ? (
                            <div className="py-8 text-center text-muted-foreground">
                                {searchQuery ? "No users found matching your search." : "No users available."}
                            </div>
                        ) : (
                            <div className="space-y-3">
                                {filteredUsers.map((user) => (
                                    <div
                                        key={user.id}
                                        className="flex items-start space-x-3 rounded-lg border p-3 hover:bg-accent"
                                    >
                                        <Checkbox
                                            checked={selectedUsers.has(user.id)}
                                            onCheckedChange={() => handleToggleUser(user.id)}
                                            className="mt-1"
                                            disabled={isIdpGroup}
                                        />
                                        <div className="flex-1 space-y-1">
                                            <div className="flex items-center gap-2">
                                                <p className="font-medium leading-none">{user.name}</p>
                                                {user.role && (
                                                    <Badge variant={user.role === "admin" ? "default" : "secondary"} className="text-xs">
                                                        {user.role}
                                                    </Badge>
                                                )}
                                            </div>
                                            <p className="text-sm text-muted-foreground">{user.email}</p>
                                            {user.department && user.department !== "-" && (
                                                <p className="text-xs text-muted-foreground">
                                                    Department: {user.department}
                                                </p>
                                            )}
                                        </div>
                                    </div>
                                ))}
                            </div>
                        )}
                    </ScrollArea>

                    {/* Selected count */}
                    <div className="text-sm text-muted-foreground">
                        {selectedUsers.size} user{selectedUsers.size !== 1 ? "s" : ""} selected
                    </div>

                    {/* Actions */}
                    <div className="flex justify-end gap-2">
                        <Button type="button" variant="outline" onClick={() => onOpenChange(false)} disabled={isSaving}>
                            {isIdpGroup ? "Close" : "Cancel"}
                        </Button>
                        {!isIdpGroup && (
                            <Button onClick={handleSave} disabled={isSaving}>
                                {isSaving && <Loader2 className="mr-2 h-4 w-4 animate-spin" />}
                                Save Changes
                            </Button>
                        )}
                    </div>
                </div>
            </DialogContent>
        </Dialog>
    );
};

export default ManageUsersDialog;
